import React from "react";

export default function Financials() {
  // Define TypeScript types
  type AnnualValue = {
    year: number;
    revenue: number | null;
    cost: number | null;
  };

  type Annotation = {
    year: number;
    x: number;
    y: number;
  };

  // Set dimensions and margins (matching the GPU chart)
  const width = 1000;
  const height = 700;
  const margin = { top: 70, right: 40, bottom: 50, left: 110 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // Parse string values to numbers (removing the 'B' and converting to billions)
  const parseValue = (strValue: string): number => {
    return parseFloat(strValue.replace("B", ""));
  };
  const parseValue2 = (strValue: string): number => {
    return parseFloat(strValue.replace("B", ""))*1.1;
  };

  // Revenue and cost data
  const revMonthlyStr: string[] = [
    "5.9B","6.6B","7.4B","8.3B","9.3B","10.3B","11.5B","12.9B","14.3B","15.9B","17.7B","19.7B",
    "21.8B","24.2B","26.8B","29.7B","32.9B","36.4B","40.2B","44.3B","48.9B","53.9B","59.4B","65.4B",
    "72.0B","79.2B","87.0B","95.6B","104.9B","115.0B","126.1B","138.1B","151.2B","165.5B","180.9B",
    "197.8B","216.0B"
  ];

  const costMonthlyStr: string[] = [
    "9.1B", "9.9B", "10.8B", "11.8B", "12.8B", "13.9B", "15.0B", "16.3B", "17.5B", "18.9B",
    "20.3B", "21.8B", "23.4B", "25.1B", "27.0B", "29.1B", "31.3B", "33.8B", "36.6B", "39.6B",
    "43.0B", "46.8B", "51.0B", "55.7B", "61.0B", "65.8B", "70.5B", "75.9B", "81.1B", "86.4B",
    "93.1B", "99.8B", "106.5B", "115.0B", "124.7B", "134.7B", "147.0B"
  ];

  // Parse to numeric values
  const revMonthly: number[] = revMonthlyStr.map(parseValue);
  const costMonthly: number[] = costMonthlyStr.map(parseValue2);

  // Create date labels (matching the format in the GPU chart)
  const generateDates = (): string[] => {
    const dates: string[] = [];
    let currentDate = new Date(2025, 0, 1); // Start from January 2025
    
    for (let i = 0; i < revMonthly.length; i++) {
        dates.push(currentDate.toLocaleDateString('en-US', { 
            month: 'short', 
            year: 'numeric' 
          }));
          currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    return dates;
  };

  const dates: string[] = generateDates();

  // Calculate annual values for annotations
  const calculateAnnualValues = (monthlyData: number[], year: number): number | null => {
    const startIndex = dates.findIndex(date => date.startsWith(year.toString()));
    if (startIndex === -1) return null;
    
    // Sum 12 months starting from January of the specified year
    const sum = monthlyData.slice(startIndex, startIndex + 12).reduce((acc, val) => acc + val, 0);
    return sum;
  };

  // Get annual values for specific years
  const annualYears: number[] = [2024, 2025, 2026, 2027];
  const annualValues: AnnualValue[] = annualYears.map(year => ({
    year,
    revenue: calculateAnnualValues(revMonthly, year),
    cost: calculateAnnualValues(costMonthly, year)
  })).filter(item => item.revenue !== null) as AnnualValue[];

  // Determine which tick indices to show on x-axis (to avoid crowding)
  const xTickIndices: number[] = [0, 6, 12, 18, 24, 30, 36];

  // Colors (matching the GPU chart style)
  const colors = {
    revenue: "#508050",  
    cost: "#000100",     
    grid: "#ddd",        // Light gray for grid
    text: "#333333",     // Dark gray for text
    axis: "#444444"      // Dark gray for axis
  };

  // Scales
  const xScale = (index: number): number => {
    return margin.left + (index / (dates.length - 1)) * innerWidth;
  };

  const yScale = (value: number): number => {
    const maxValue = 220; // Just above the max value in our data
    return margin.top + innerHeight - (value / maxValue) * innerHeight;
  };

  // Generate line paths
  const generateLinePath = (data: number[]): string => {
    return data.map((value, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(value)}`
    ).join(' ');
  };

  const revenuePath: string = generateLinePath(revMonthly);
  const costPath: string = generateLinePath(costMonthly);

  // Generate y-axis ticks at $50B intervals
  const yTicks: number[] = [0, 50, 100, 150, 200];

  // Determine positions for annotations
  const annotations: Annotation[] = [
    { year: 2024, x: xScale(3), y: yScale(revMonthly[18])},
    { year: 2025, x: xScale(12)+10, y: yScale(revMonthly[21])},
    { year: 2026, x: xScale(24), y: yScale(revMonthly[14])+10},
    { year: 2027, x: xScale(34)-10, y: yScale(revMonthly[25])+10}
  ];

  return (
    <div className="w-full relative overflow-hidden mt-8 mb-8">
      <svg
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Chart Title */}
        <text
          x={margin.left}
          y={40}
          fontSize="32"
          fontFamily="sans-monospace"
          className="text-[32px] font-mono md:text-[32px] sm:text-[32px]"
          fill={colors.text}
        >
          Leading AI Company Revenue & Cost Projections
        </text>

        {/* Y-axis Label */}
        <text
          transform={`rotate(-90)`}
          y={10}
          x={-height / 2}
          dy="1em"
          textAnchor="middle"
          className="text-[22px] font-mono md:text-[22px] sm:text-[22px]"
          fill={colors.text}
        >
          Value (USD)
        </text>

        {/* X-axis ticks and labels */}
        {xTickIndices.map((index) => {
          const x = xScale(index);
          return (
            <g key={`x-tick-${index}`}>
              <line 
                x1={x} 
                y1={height - margin.bottom} 
                x2={x} 
                y2={margin.top + 20} 
                stroke={colors.axis}
                strokeWidth="0.5" 
                strokeDasharray="5,5"
                opacity="0.15"
              />
              <text 
                x={x} 
                y={height - margin.bottom / 2} 
                textAnchor="middle" 
                className="text-[16px] font-mono md:text-[16px] sm:text-[16px]"
                fill={colors.text}
              >
                {dates[index]}
              </text>
            </g>
          );
        })}

        {/* Horizontal grid lines */}
        {yTicks.map((tick, i) => (
          <g key={`grid-${i}`}>
            <line 
              x1={margin.left} 
              y1={yScale(tick)} 
              x2={width - margin.right} 
              y2={yScale(tick)} 
              stroke={colors.grid} 
              strokeWidth="0.8" 
              strokeDasharray="5,5"
            />
            
            <text 
              x={margin.left - 10} 
              y={yScale(tick) + 4} 
              textAnchor="end" 
              className="text-[18px] font-mono md:text-[18px] sm:text-[18px]"
              fill={colors.text}
            >
              {tick === 0 ? "0" : `$${tick}B`}
            </text>
          </g>
        ))}

        {/* Revenue Line */}
        <path
          d={revenuePath}
          fill="none"
          stroke={colors.revenue}
          strokeWidth="2.5"
        />

        {/* Cost Line */}
        <path
          d={costPath}
          fill="none"
          stroke={colors.cost}
          strokeWidth="2.5"
        />

        {/* Data Points */}
        {revMonthly.map((value, i) => (
          <circle
            key={`rev-point-${i}`}
            cx={xScale(i)}
            cy={yScale(value)}
            r="4"
            fill={colors.revenue}
          />
        ))}

        {costMonthly.map((value, i) => (
          <circle
            key={`cost-point-${i}`}
            cx={xScale(i)}
            cy={yScale(value)}
            r="4"
            fill={colors.cost}
          />
        ))}

        {/* Legend */}
        <g transform={`translate(${margin.left+15}, 75)`}>
          {/* Revenue */}
          <rect x="0" y="0" width="20" height="20" fill={colors.revenue} />
          <text x="30" y="15" fill={colors.text} className="text-[16px] font-mono">Annualized Revenue</text>
          
          {/* Cost */}
          <rect x="0" y="30" width="20" height="20" fill={colors.cost} />
          <text x="30" y="45" fill={colors.text} className="text-[16px] font-mono">Annualized Compute Costs</text>
        </g>

        {/* Annual Annotations */}
        {annotations.map((anno, i) => {
        //   const annualData = annualValues.find(v => v.year === anno.year);
        //   if (!annualData || annualData.revenue === null || annualData.cost === null) return null;
          
          return (
            <g key={`annotation-${i}`} transform={`translate(${anno.x}, ${anno.y})`}>
              <rect
                x="-50"
                y="-50"
                width="100"
                height="100"
                fill="white"
                stroke={colors.grid}
                strokeWidth="1"
                rx="5"
                ry="5"
              />
              <text
                x="0"
                y="-30"
                textAnchor="middle"
                className="text-[16px] font-mono"
                fill={colors.text}
              >
                Annual
              </text>
              <text
                x="0"
                y="-10"
                textAnchor="middle"
                className="text-[16px] font-mono"
                fill={colors.text}
              >
                {anno.year}
              </text>
              <text
                x="0"
                y="20"
                textAnchor="middle"
                className="text-[16px] font-mono"
                fill={colors.revenue}
                fontWeight="bold"
              > 
              ${((anno.year===2024) ? 4 : ((anno.year===2025) ? 14 : ((anno.year===2026) ? 50 : 150)))}B
              </text>
              <text
                x="0"
                y="45"
                textAnchor="middle"
                className="text-[16px] font-mono"
                fill={colors.cost}
                fontWeight="bold"
              >
                ${((anno.year===2024) ? 6: ((anno.year===2025) ? 20 : ((anno.year===2026) ? 50 : 120)))}B
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}